import { useMutation, UseMutationResult } from 'react-query';
import { gql } from '@apollo/client';
import { graphqlClient } from 'bk-web-app';
import { ErrorWithData } from 'task-manager/bench-services-api/ErrorWithData';
import { BusinessService, EditBusinessServiceInput } from '../types';

const UPDATE_CLIENT_SERVICE_MUTATION = gql`
  mutation UpdateClientService($id: ID!, $input: UpdateClientServiceInput!) {
    updateClientService(id: $id, input: $input) {
      id
      client {
        id
      }
      globalService {
        id
      }
      addOn {
        id
      }
      uniqueIdentifier
      effectiveStartDate
      effectiveEndDate
      entityVersion
    }
  }
`;

interface UpdateClientServiceResponse {
  updateClientService: {
    id: string;
    client: { id: string };
    globalService: { id: string };
    addOn?: { id: string } | null;
    uniqueIdentifier?: string | null;
    effectiveStartDate?: string;
    effectiveEndDate?: string;
    entityVersion?: string;
  };
}

function mapClientServiceToBusinessService(
  clientService: UpdateClientServiceResponse['updateClientService'],
): BusinessService {
  return {
    businessServiceId: clientService.id,
    businessId: Number(clientService.client.id),
    globalServiceId: clientService.globalService.id,
    uniqueIdentifier: clientService.uniqueIdentifier,
    effectiveStartDate: clientService.effectiveStartDate,
    effectiveEndDate: clientService.effectiveEndDate,
    addOnId: clientService.addOn?.id ?? '',
    createdAt: '',
    createdBy: '',
  };
}

async function editViaGraphQL(input: EditBusinessServiceInput): Promise<BusinessService> {
  const { data } = await graphqlClient.mutate<UpdateClientServiceResponse>({
    mutation: UPDATE_CLIENT_SERVICE_MUTATION,
    variables: {
      id: input.businessServiceId,
      input: {
        globalServiceId: String(input.globalServiceId),
        addOnId: String(input.addOnId),
        uniqueIdentifier: input.uniqueIdentifier,
        effectiveStartDate: input.effectiveStartDate,
        effectiveEndDate: input.effectiveEndDate,
      },
    },
    context: { useV2: true },
  });

  if (!data?.updateClientService) {
    throw new Error('Failed to update client service via GraphQL');
  }
  return mapClientServiceToBusinessService(data.updateClientService);
}

async function edit(apiUrl: string, input: EditBusinessServiceInput): Promise<BusinessService> {
  if (!apiUrl) throw new Error('Required properties are not set');

  const response: Response = await fetch(`${apiUrl}/business-service/${input.businessServiceId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = (await response.json()) as Record<string, unknown>;
    throw new ErrorWithData(
      'Failed to edit business service. Try a different identifier to make sure no two services are the same. If the problem persists contact support. ',
      error,
    );
  }
  return (await response.json()) as BusinessService;
}

interface UseEditBusinessServiceProps {
  handleError: (error: Error) => void;
  useGraphQL?: boolean;
}

export const useEditBusinessService = ({
  handleError,
  useGraphQL = false,
}: UseEditBusinessServiceProps): UseMutationResult<
  BusinessService,
  Error,
  EditBusinessServiceInput
> => {
  const apiUrl = window.BENCH?.auApi?.SETUP_BUSINESS_SERVICE_API_URL || '';

  return useMutation({
    mutationFn: input => (useGraphQL ? editViaGraphQL(input) : edit(apiUrl, input)),
    onError: handleError,
  });
};
